<script setup>
import { ref, computed } from 'vue'
import Button from './ui/Button.vue'
import { XIcon, CheckIcon } from './Icons.js'
import { fmt, getLineTotalWithVat } from '../utils/money'

const props = defineProps({
	isOpen: Boolean,
	maxRefundable: Number,
	currency: String,
	items: Array,
})

const emit = defineEmits(['close', 'submit'])

// ------------------------------
// STATE
// ------------------------------
const totalRefundInput = ref('')
const reason = ref('')
const useItemBreakdown = ref(false)
const itemRefunds = ref({})

// ------------------------------
// COMPUTED
// ------------------------------
const totalRefundKop = computed(() => {
	if (useItemBreakdown.value) {
		return Object.values(itemRefunds.value)
			.reduce((sum, v) => sum + Math.round((parseFloat(v) || 0) * 100), 0)
	}
	return Math.round((parseFloat(totalRefundInput.value) || 0) * 100)
})

const isOverLimit = computed(() => totalRefundKop.value > props.maxRefundable)
const isValid = computed(() =>
	totalRefundKop.value > 0 &&
	!isOverLimit.value &&
	reason.value.trim().length > 0
)

// ------------------------------
// METHODS
// ------------------------------
const handleTotalChange = (val) => {
	totalRefundInput.value = val
	if (useItemBreakdown.value) itemRefunds.value = {}
}

const handleItemRefundChange = (id, val) => {
	itemRefunds.value = { ...itemRefunds.value, [id]: val }

	const sum = Object.values(itemRefunds.value)
		.reduce((acc, v) => acc + (parseFloat(v) || 0), 0)

	totalRefundInput.value = sum.toFixed(2)
}

const submit = () => {
	const items = useItemBreakdown.value
		? Object.entries(itemRefunds.value).map(([id, v]) => ({
			itemId: Number(id),
			amount: Math.round((parseFloat(v) || 0) * 100),
		}))
		: props.items.map(item => ({
			itemId: item.itemId,
			amount: Math.round((parseFloat(totalRefundInput.value) || 0) * 100)
		}))

	const sum = items
		? items.reduce((acc, i) => acc + i.amount, 0)
		: totalRefundKop.value

	emit('submit', {
		sum,
		reason: reason.value,
		items,
	})
}

</script>

<template>
	<div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
		<div class="bg-white w-full max-w-lg rounded-xl shadow-2xl border border-slate-200 m-4 flex flex-col max-h-[90vh]">

			<!-- HEADER -->
			<div class="flex items-center justify-between p-5 border-b border-slate-100">
				<h3 class="font-semibold text-lg">{{ $t('invoices.refund_modal_title') }}</h3>
				<button @click="emit('close')" class="text-slate-400 hover:text-slate-600">
					<XIcon class="w-5 h-5" />
				</button>
			</div>

			<!-- BODY -->
			<div class="p-6 space-y-6 overflow-y-auto">

				<!-- MAX -->
				<div class="bg-blue-50/50 border border-blue-100 p-4 rounded-lg flex justify-between">
					<span class="text-sm text-blue-700">{{ $t('invoices.refund_max_amount') }}</span>
					<span class="font-bold">
						{{ (maxRefundable / 100).toFixed(2) }} {{ currency }}
					</span>
				</div>

				<!-- TOTAL INPUT -->
				<div>
					<label class="text-xs font-bold uppercase">{{ $t('invoices.refund_amount') }}</label>
					<input
						type="number"
						class="w-full mt-1 border rounded-lg px-4 py-3 text-xl font-bold"
						:class="isOverLimit ? 'border-red-300 bg-red-50' : 'border-slate-200'"
						:value="totalRefundInput"
						@input="handleTotalChange($event.target.value)"
						:disabled="useItemBreakdown"
					/>
					<p v-if="isOverLimit" class="text-xs text-red-600 mt-1">
						{{ $t('invoices.refund_error_max') }}
					</p>
				</div>

				<!-- REASON -->
				<div>
					<label class="text-xs font-bold uppercase">{{ $t('invoices.refund_reason') }}</label>
					<textarea
						v-model="reason"
						class="w-full h-24 border rounded-lg px-3 py-2 text-sm"
					/>
				</div>

				<!-- ITEM BREAKDOWN -->
				<div v-if="items.length">
					<label class="flex items-center gap-2 cursor-pointer">
						<div class="w-5 h-5 border rounded flex items-center justify-center"
								 :class="useItemBreakdown ? 'bg-slate-900 text-white' : ''">
							<CheckIcon v-if="useItemBreakdown" class="w-3 h-3" />
						</div>
						<input type="checkbox" class="hidden" v-model="useItemBreakdown" />
						<span class="text-sm">{{ $t('invoices.refund_items_breakdown') }}</span>
					</label>

					<div v-if="useItemBreakdown" class="mt-4 space-y-2">
						<div
							v-for="item in items"
							:key="item.itemId"
							class="flex justify-between items-center border rounded p-2"
						>
							<div>
								<div class="font-medium">{{ item.name }}</div>
								<div class="text-xs text-slate-400">
									{{ fmt(getLineTotalWithVat(item)) }} {{ currency }}
								</div>
							</div>
							<input
								type="number"
								class="w-24 border rounded px-2 py-1 text-right"
								:value="itemRefunds[item.itemId] || ''"
								@input="handleItemRefundChange(item.itemId, $event.target.value)"
							/>
						</div>
					</div>
				</div>

			</div>

			<!-- FOOTER -->
			<div class="p-5 border-t bg-slate-50 flex justify-end gap-3">
				<Button variant="outline" @click="emit('close')">
					{{ $t('invoices.cancel') }}
				</Button>
				<Button
					:disabled="!isValid"
					@click="submit"
					:class="isValid ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-400'"
				>
					{{ $t('invoices.refund_submit') }}
				</Button>
			</div>

		</div>
	</div>
</template>
